// Which element the green corners lock onto (shared by the mouse cursor and the touch corners).
// Returns { el, line } — `line: true` means a frameless text link: the corners spread into a
// line under the word instead of sitting on four corners.
(() => {
  const fits = (el) => {
    const r = el.getBoundingClientRect();
    return r.width > 4 && r.height > 4 && r.width < Math.min(720, window.innerWidth * 0.95) && r.height < window.innerHeight * 0.6;
  };
  const box = (el) => (el && fits(el) ? { el, line: false } : null);
  // buttons can be full width ("View all", load more): only their height has to be reasonable
  const boxWide = (el) => { if (!el) return null; const r = el.getBoundingClientRect(); return r.width > 4 && r.height > 4 && r.height < 160 ? { el, line: false } : null; };

  window.xoSnapTarget = (node) => {
    const el = node instanceof Element ? node : null;
    if (!el) return null;
    // text boxes (search and every other field): the corners lock onto the box's frame
    const field = el.closest('input:not([type="radio"]):not([type="checkbox"]):not([type="range"]):not([type="hidden"]), textarea, [contenteditable="true"]');
    if (field) {
      if (field.closest('#predictive-search')) return null; // the quick-search panel field gets no corners
      const qtyBox = field.closest('s-quantity-selector'); // product-page quantity number: corners on the whole frame, like in the cart
      if (qtyBox) { const q = qtyBox.getBoundingClientRect(); return q.width > 4 && q.height > 4 ? { el: qtyBox, line: false } : null; }
      let framed = field;
      for (let n = field, i = 0; n && i < 4; n = n.parentElement, i++) {
        if (parseFloat(getComputedStyle(n).borderTopWidth) > 0 || parseFloat(getComputedStyle(n).borderBottomWidth) > 0) { framed = n; break; }
      }
      const r = framed.getBoundingClientRect();
      return r.width > 4 && r.height > 4 && r.height < 200 ? { el: framed, line: false } : null;
    }
    // video progress bar: the corners lock onto the whole bar (it is only 4px tall, so no size check)
    const seek = el.closest('.xo-vid-seek');
    if (seek) { const s = seek.getBoundingClientRect(); return s.width > 4 ? { el: seek, line: false } : null; }
    if (el.closest('select')) return null;
    // dropdown headers (filters, menu): corners only on the arrow, not the whole row
    const summary = el.closest('summary');
    if (summary) {
      const arrow = summary.querySelector('svg');
      return arrow ? box(arrow) : null;
    }

    if (el.closest('[data-xo-no-corners]')) return null;

    // cart product rows (cart page and cart panel): only the photo or the product name puts the corners on the photo
    const cartLine = el.closest('.xo-cart-line');
    if (cartLine && el.closest('a[href]') && !el.closest('button, s-quantity-selector, input')) {
      const thumb = cartLine.querySelector('a.xo-cart-thumb');
      if (thumb) return box(thumb);
    }

    // product / collection images
    const card = el.closest('article.group\\/card');
    if (card) {
      const img = card.querySelector(':scope > a:not(.absolute)');
      if (img) return box(img);
    }
    const tile = el.closest('a.group\\/tile');
    if (tile) return box(tile.querySelector('.xo-tile-frame'));

    // product options (capacity / colour)
    const option = el.closest('s-variant-picker label');
    if (option) {
      const input = option.querySelector('input');
      if (input && input.disabled) return null;
      return box(input?.nextElementSibling);
    }

    // filter choices (checkbox rows, colour swatches) and any other checkbox / radio label
    const choice = el.closest('label');
    if (choice && choice.querySelector('input[type="checkbox"], input[type="radio"]')) {
      const input = choice.querySelector('input');
      if (input.disabled) return null;
      return box(input.nextElementSibling || choice); // the little square / swatch itself
    }

    // quantity selector: corners on the + or − button pressed (not the whole frame, not the number)
    const qty = el.closest('s-quantity-selector');
    if (qty) {
      const step = el.closest('button');
      return step ? box(step.querySelector('svg') || step) : null; // hug the + / − icon itself
    }

    // header sections (tabs)
    const tab = el.closest('[data-xo-tabs] > a');
    if (tab) return box(tab);

    // active filter chips: the corners sit on the X only; «مسح الكل»: a line under the words
    const filterChip = el.closest('.xo-filter-chip');
    if (filterChip) { const x = filterChip.querySelector('svg'); return x ? box(x) : boxWide(filterChip); }
    const clearAll = el.closest('.xo-clear-all');
    if (clearAll) return fits(clearAll) ? { el: clearAll, line: true } : null;

    // store logo: corners around it (desktop hover and phone tap)
    const logo = el.closest('[data-xo-logo]');
    if (logo) return box(logo);

    // recent-search chips in the search panel: the X gets the corners on its own; the word puts them on the whole chip
    const historyChip = el.closest('.xo-history-chip');
    if (historyChip) {
      const removeBtn = el.closest('[command="--remove"]');
      if (removeBtn) { const x = removeBtn.querySelector('svg'); return x ? box(x) : null; }
      return boxWide(historyChip);
    }

    // brand chips
    const chip = el.closest('.xo-brand-chip');
    if (chip) return box(chip);

    // "More payment options" under the buy buttons: a line under the words, like the policy links
    const more = el.closest('.shopify-payment-button__more-options');
    if (more) return fits(more) ? { el: more, line: true } : null;

    // express pay buttons (PayPal etc. — a frame from another site): corners on the whole button
    const payWrap = el.closest('.shopify-payment-button, [data-shopify="payment-button"], shopify-accelerated-checkout');
    if (payWrap) {
      const b = el.closest('.shopify-payment-button__button') || (el.tagName === 'IFRAME' ? el : null) || payWrap.querySelector('.shopify-payment-button__button:not(.shopify-payment-button__more-options)') || el;
      return boxWide(b);
    }

    // buy buttons, cart, icons and every other button
    const btn = el.closest('button, [role="button"], .shopify-payment-button__button, a.xo-icon-link, #header-cart-trigger, a[class*="button"], a.border, s-load-more a, a.xo-btn-primary, a.xo-btn-secondary, a.xo-gift-shop, a.xo-gift-shop-big, a.xo-gift-btn');
    if (btn) {
      // icon-only buttons (X, menu, search, cart): hug the icon itself so the corners sit tight on it
      const icon = btn.querySelector('svg');
      const r = btn.getBoundingClientRect();
      const framed = parseFloat(getComputedStyle(btn).borderTopWidth) > 0;
      if (icon && !framed && r.width <= 60 && r.height <= 60) return box(icon); // framed buttons keep corners on the frame
      // text-only buttons with no frame and no fill (e.g. «مسح» in the search panel): a line under the word
      // on desktop, like text links (on phones the word just turns green)
      const bs = getComputedStyle(btn);
      const filled = bs.backgroundColor !== 'rgba(0, 0, 0, 0)' && bs.backgroundColor !== 'transparent';
      if (!icon && !framed && !filled && btn.textContent.trim() && r.height <= 60 && fits(btn)) return { el: btn, line: true };
      return boxWide(btn);
    }

    // icon-only links (social icons, logo)
    const iconLink = el.closest('a');
    if (iconLink && iconLink.querySelector('svg, img') && !iconLink.textContent.trim()) return box(iconLink);

    // side-menu product rows: corners around the whole row (desktop); on touch only the text turns green
    const navProduct = el.closest('.xo-nav-products a');
    if (navProduct) return boxWide(navProduct);

    // frameless text links: footer, policies, pages, brand link → line under the word
    const text = el.closest('.xo-footer-link, footer a, .richtext a, .shopify-policy__container a, nav a');
    if (text && fits(text)) return { el: text, line: true };
    return null;
  };
  // Never lock onto something the visitor can't see (faded-out buttons, closed panels, hidden menus):
  // otherwise the corners hang on an empty spot.
  const pick = window.xoSnapTarget;
  window.xoSnapSeen = (el) => {
    if (!el || !el.isConnected) return false;
    if (el.checkVisibility) return el.checkVisibility({ opacityProperty: true, visibilityProperty: true });
    const s = getComputedStyle(el);
    return s.visibility !== 'hidden' && s.display !== 'none' && parseFloat(s.opacity) > 0.05;
  };
  window.xoSnapTarget = (node) => {
    const info = pick(node);
    return info && window.xoSnapSeen(info.el) ? info : null;
  };
})();
